import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  Form, 
  Input, 
  Button, 
  Select,
  DatePicker,
  message,
  Typography,
  Avatar,
  Spin,
  Divider,
  Space,
  Collapse
} from 'antd';
import { SaveOutlined, UndoOutlined, ArrowLeftOutlined, PlusOutlined } from '@ant-design/icons';
import PageLayout from '../../../components/layout/PageLayout';
import tvShowService from '../../../services/tvShowService';
import masterService from '../../../services/masterService';
import { useUser } from '../../../context/UserContext';
import moment from 'moment';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { Panel } = Collapse;

const AddTvShow = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [form] = Form.useForm();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [spinning, setSpinning] = useState(false);
  
  // Dropdown data
  const [genres, setGenres] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [tags, setTags] = useState([]);

  // Seasons management
  const [seasons, setSeasons] = useState([
    {
      season_no: 1,
      season_title: '',
      season_description: '',
      season_casts: '',
      season_directors: '',
      season_original_release_date: null,
      season_release_date: null,
      season_is_active: 'Y'
    }
  ]);

  // Load dropdown data
  useEffect(() => {
    const loadDropdownData = async () => {
      try {
        setLoading(true);
        
        const [genresRes, ratingsRes, languagesRes, tagsRes] = await Promise.all([
          masterService.getGenres(),
          masterService.getVideoRatings(),
          masterService.getVideoLanguages(),
          masterService.getTagList()
        ]);

        if (genresRes.data?.status) {
          setGenres(genresRes.data.data || []);
        }
        if (ratingsRes.data?.status) {
          setRatings(ratingsRes.data.data || []);
        }
        if (languagesRes.data?.status) {
          setLanguages(languagesRes.data.data || []);
        }
        if (tagsRes.data?.status) {
          setTags(tagsRes.data.data || []);
        }
      } catch (error) {
        console.error('Error loading dropdown data:', error);
        message.error('Failed to load form data');
      } finally {
        setLoading(false);
      }
    };

    loadDropdownData();
  }, []);

  // Handle form submission
  const handleSubmit = async (values) => {
    try {
      setSpinning(true);
      
      // Format dates and prepare data
      const formattedValues = {
        ...values,
        genre_list: Array.isArray(values.genre_list) ? values.genre_list.join(',') : values.genre_list,
        lang_list: Array.isArray(values.lang_list) ? values.lang_list.join(',') : values.lang_list,
        tags_list: Array.isArray(values.tags_list) ? values.tags_list.join(',') : values.tags_list,
        seasons_list: seasons
      };

      console.log('Submitting TV show data:', formattedValues);
      const response = await tvShowService.addTvShow(formattedValues);
      
      if (response.data && response.data.status) {
        message.success(response.data.message || 'TV Show added successfully');
        form.resetFields();
        navigate('/TvShows/Shows');
      } else {
        message.error(response.data.message || 'Failed to add TV show');
      }
    } catch (error) {
      console.error('Error adding TV show:', error);
      message.error('Failed to add TV show');
    } finally {
      setSpinning(false);
    }
  };

  // Handle form reset
  const handleReset = () => {
    form.resetFields();
    setSeasons([
      {
        season_no: 1,
        season_title: '',
        season_description: '',
        season_casts: '',
        season_directors: '',
        season_original_release_date: null,
        season_release_date: null,
        season_is_active: 'Y'
      }
    ]);
  };

  // Handle back navigation
  const handleBack = () => {
    navigate('/TvShows/Shows');
  };

  // Add new season
  const addSeason = () => {
    setSeasons(prev => [
      ...prev,
      {
        season_no: prev.length + 1,
        season_title: '',
        season_description: '',
        season_casts: '',
        season_directors: '',
        season_original_release_date: null,
        season_release_date: null,
        season_is_active: 'Y'
      }
    ]);
  };

  // Update season data
  const updateSeason = (index, field, value) => {
    setSeasons(prev => prev.map((season, i) => 
      i === index ? { ...season, [field]: value } : season
    ));
  };

  // Remove season
  const removeSeason = (index) => {
    if (seasons.length > 1) {
      setSeasons(prev => prev.filter((_, i) => i !== index));
    }
  };

  // Header content with user info
  const headerContent = (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <Avatar 
          src={user?.avatar} 
          size={64} 
          style={{ marginRight: '16px' }}
        >
          {user?.name?.charAt(0)?.toUpperCase()}
        </Avatar>
        <div>
          <Title level={4} style={{ margin: 0, marginBottom: '4px' }}>
            {user?.name || 'Admin'}
          </Title>
          <Text type="secondary">Add New TV Show</Text>
        </div>
      </div>
    </div>
  );

  return (
    <PageLayout
      title="Add TV Show"
      desc="Create a new TV show with all details"
      avatar={user?.avatar}
      headerContent={headerContent}
    >
      <Spin spinning={spinning}>
        <Card>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              is_subscription_required: 'N',
              tv_show_is_active: 'Y'
            }}
          >
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <Form.Item
                name="tv_show_name"
                label="Series Name"
                rules={[{ required: true, message: 'Please enter series name' }]}
              >
                <Input placeholder="Enter series name" />
              </Form.Item>

              <Form.Item
                name="is_subscription_required"
                label="Is Subscription Required"
                rules={[{ required: true, message: 'Please select subscription requirement' }]}
              >
                <Select placeholder="Select subscription requirement">
                  <Option value="Y">Yes</Option>
                  <Option value="N">No</Option>
                </Select>
              </Form.Item>
            </div>

            <Form.Item
              name="summary"
              label="Summary"
            >
              <TextArea 
                rows={4} 
                placeholder="Enter series summary"
                maxLength={2000}
                showCount
              />
            </Form.Item>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <Form.Item
                name="director"
                label="Director"
                rules={[{ required: true, message: 'Please enter director name' }]}
              >
                <Input placeholder="Enter director name" />
              </Form.Item>

              <Form.Item
                name="cast_members"
                label="Cast Members"
                rules={[{ required: true, message: 'Please enter cast members' }]}
              >
                <Input placeholder="Enter cast members" />
              </Form.Item>
            </div>

            <Form.Item
              name="rating_description"
              label="Rating Description"
            >
              <Input placeholder="Enter rating description" />
            </Form.Item>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <Form.Item
                name="lang_list"
                label="Show Languages"
                rules={[{ required: true, message: 'Please select languages' }]}
              >
                <Select
                  mode="multiple"
                  placeholder="Select languages"
                  loading={loading}
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {languages.map(lang => (
                    <Option key={lang.lang_id} value={lang.lang_id}>
                      {lang.lang_name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="genre_list"
                label="Show Genre"
                rules={[{ required: true, message: 'Please select genre' }]}
              >
                <Select
                  mode="multiple"
                  placeholder="Select genres"
                  loading={loading}
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {genres.map(genre => (
                    <Option key={genre.genre_id} value={genre.genre_id}>
                      {genre.genre_name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <Form.Item
                name="tags_list"
                label="Tags"
              >
                <Select
                  mode="multiple"
                  placeholder="Select tags"
                  loading={loading}
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {tags.map(tag => (
                    <Option key={tag.tag_id} value={tag.tag_id}>
                      {tag.tag_name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="rating_id"
                label="Rating"
                rules={[{ required: true, message: 'Please select rating' }]}
              >
                <Select
                  placeholder="Select rating"
                  loading={loading}
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {ratings.map(rating => (
                    <Option key={rating.vr_id} value={rating.vr_id}>
                      {rating.rating_name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </div>

            <Form.Item
              name="tv_show_is_active"
              label="Status"
              rules={[{ required: true, message: 'Please select status' }]}
            >
              <Select placeholder="Select status">
                <Option value="Y">Active</Option>
                <Option value="N">Inactive</Option>
              </Select>
            </Form.Item>

            <Divider />

            <Form.Item>
              <Space>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  icon={<SaveOutlined />}
                  loading={spinning}
                  size="large"
                >
                  Save Series
                </Button>
                <Button 
                  onClick={handleReset}
                  icon={<UndoOutlined />}
                  size="large"
                >
                  Reset
                </Button>
                <Button 
                  onClick={handleBack}
                  icon={<ArrowLeftOutlined />}
                  size="large"
                >
                  Go Back
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>

        {/* Seasons Section */}
        <Card style={{ marginTop: '24px' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '16px'
          }}>
            <Title level={4} style={{ margin: 0 }}>
              Seasons Management
            </Title>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={addSeason}
              size="large"
            >
              Add Season
            </Button>
          </div>

          <Collapse>
            {seasons.map((season, index) => (
              <Panel 
                header={`Season ${season.season_no}`} 
                key={index}
                extra={
                  seasons.length > 1 && (
                    <Button 
                      type="link" 
                      danger 
                      onClick={(e) => {
                        e.stopPropagation();
                        removeSeason(index);
                      }}
                    >
                      Remove
                    </Button>
                  )
                }
              >
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label>Season Title</label>
                    <Input
                      value={season.season_title}
                      onChange={(e) => updateSeason(index, 'season_title', e.target.value)}
                      placeholder="Enter season title"
                    />
                  </div>

                  <div>
                    <label>Season Status</label>
                    <Select
                      value={season.season_is_active}
                      onChange={(value) => updateSeason(index, 'season_is_active', value)}
                      style={{ width: '100%' }}
                    >
                      <Option value="Y">Active</Option>
                      <Option value="N">Inactive</Option>
                    </Select>
                  </div>
                </div>

                <div style={{ marginTop: '16px' }}>
                  <label>Season Description</label>
                  <TextArea
                    value={season.season_description}
                    onChange={(e) => updateSeason(index, 'season_description', e.target.value)}
                    placeholder="Enter season description"
                    rows={3}
                    maxLength={2000}
                    showCount
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '16px' }}>
                  <div>
                    <label>Cast Members</label>
                    <Input
                      value={season.season_casts}
                      onChange={(e) => updateSeason(index, 'season_casts', e.target.value)}
                      placeholder="Enter cast members"
                    />
                  </div>

                  <div>
                    <label>Directors</label>
                    <Input
                      value={season.season_directors}
                      onChange={(e) => updateSeason(index, 'season_directors', e.target.value)}
                      placeholder="Enter directors"
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '16px' }}>
                  <div>
                    <label>Original Release Date</label>
                    <DatePicker
                      value={season.season_original_release_date ? moment(season.season_original_release_date) : null}
                      onChange={(date) => updateSeason(index, 'season_original_release_date', date)}
                      showTime
                      format="YYYY-MM-DD HH:mm:ss"
                      placeholder="Select original release date"
                      style={{ width: '100%' }}
                    />
                  </div>

                  <div>
                    <label>Release Date</label>
                    <DatePicker
                      value={season.season_release_date ? moment(season.season_release_date) : null}
                      onChange={(date) => updateSeason(index, 'season_release_date', date)}
                      showTime
                      format="YYYY-MM-DD HH:mm:ss"
                      placeholder="Select release date"
                      style={{ width: '100%' }}
                    />
                  </div>
                </div>
              </Panel>
            ))}
          </Collapse>
        </Card>
      </Spin>
    </PageLayout>
  );
};

export default AddTvShow;